import { FilterTypes } from "../../../../types";
import { DateTime } from "luxon";
import { parseScheduleDateFilter } from "../../schedule";
import { ATTRIBUTION_MODEL_FIELDS } from "./configuration";
export { ATTRIBUTION_MODEL_FIELDS };
// The looker-gateway attribution query contract. These modules take only types
// from this one, so the re-export is cycle-free.
export * from "./api";
export * from "./compatibility";
export * from "./policy";
// The looker-gateway delivery query contract — a field-restricted view onto
// the same explore as cross-channel attribution. Depends on ./api for the
// shared snake_case map and cross-channel filter/required-filter types.
export * from "./delivery";
/**
 * Universal Pixel event-type stage labels. Must match the values returned by
 * Looker's `event_type_label` CASE WHEN in
 * `views/team_insights/conversion/explore_conversion.view.lkml`. Canonical UP
 * event-type vocabulary — shared across Attribution + Insights surfaces.
 */
export const EVENT_TYPE_STAGE_LABELS = [
    "Page View",
    "Contact",
    "Form Submission",
    "Add to Cart",
    "Purchase"
];
export const ATTRIBUTION_MODEL_TO_QUERY_MAP = {
    mediaType: "explore_conversion.media_type",
    totalImpressions: "explore_conversion.impressions",
    totalCompletes: "explore_conversion.completes",
    totalReach: "explore_conversion.reach",
    averageFrequency: "explore_conversion.avg_frequency",
    totalConversions: "explore_conversion.impression_converted",
    conversionRate: "explore_conversion.conversion_rate",
    uniqueConversions: "explore_conversion.unique_conversions",
    uniqueConversionRate: "explore_conversion.unique_conversion_rate",
    viewCompletionRate: "explore_conversion.view_completion_rate",
    listenThroughRate: "explore_conversion.listen_through_rate",
    totalClicks: "explore_conversion.clicks",
    clickThroughRate: "explore_conversion.click_through_rate",
    publisherName: "explore_conversion.publisher_name",
    creativeName: "explore_conversion.creative_name",
    creativeId: "explore_conversion.creative_id",
    beaconName: "explore_conversion.beacon_name",
    beaconId: "explore_conversion.beacon_id",
    beaconUrl: "explore_conversion.beacon_url",
    beaconPath: "explore_conversion.beacon_path",
    pageCategory: "explore_conversion.page_category",
    pageCategoryAdvertiserVertical: "explore_conversion.vertical_page_category",
    vin: "explore_conversion.vin",
    make: "explore_conversion.make",
    model: "explore_conversion.model",
    year: "explore_conversion.year",
    conversionType: "explore_conversion.conversion_type_label",
    eventType: "explore_conversion.event_type_label",
    formName: "explore_conversion.form_name",
    buttonName: "explore_conversion.button_name",
    contactType: "explore_conversion.contact_type",
    cartItems: "explore_conversion.cart_items",
    orderId: "explore_conversion.order_id",
    daysToConversion: "explore_conversion.days_to_conversion",
    state: "explore_conversion.region_name",
    stateCode: "explore_conversion.region_code",
    creativeSize: "explore_conversion.creative_size",
    lineitemName: "explore_conversion.lineitem_name",
    lineitemId: "explore_conversion.lineitem_id",
    optimizationType: "explore_conversion.optimization_type",
    isOptimized: "explore_conversion.is_optimized",
    zipCode: "explore_conversion.postal",
    week: "explore_conversion.impression_week",
    month: "explore_conversion.impression_month",
    monthOfYear: "explore_conversion.impression_month_of_year",
    hourOfWeek: "explore_conversion.impression_hour_of_week",
    hourOfDay24: "explore_conversion.impression_hour_of_day24",
    hourOfDay12: "explore_conversion.impression_hour_of_day12",
    dayOfWeek: "explore_conversion.impression_day_of_week",
    dayOfMonth: "explore_conversion.impression_day_of_month",
    date: "explore_conversion.impression_date",
    frequency: "explore_conversion.frequency",
    metro: "explore_conversion.metro_name",
    metroCode: "explore_conversion.metro_code",
    deviceType: "explore_conversion.device_type",
    subtype: "explore_conversion.media_subtype",
    daypart: "explore_conversion.daypart_name",
    congressionalDistrict: "explore_conversion.congressional_district_name",
    congressionalDistrictCode: "explore_conversion.congressional_district_code",
    campaignName: "explore_conversion.campaign_name",
    campaignId: "explore_conversion.campaign_id",
    advertiserName: "explore_conversion.advertiser_name",
    contentLanguage: "explore_conversion.language_name",
    bucketDateRange: "explore_conversion.bucket_date_range",
    mediaCost: "explore_conversion.media_cost",
    audienceCost: "explore_conversion.audience_cost",
    totalSpend: "explore_conversion.total_spend",
    costPerMille: "explore_conversion.cpm",
    costPerConversion: "explore_conversion.cpc",
    costPerClick: "explore_conversion.cost_per_click",
    costPerCompletedView: "explore_conversion.cpcv",
    supplementalCost: "explore_conversion.supplemental_cost",
    footfallVisitorsImpressionTime: "explore_conversion.footfall_visitors_credits",
    footfallVisitsImpressionTime: "explore_conversion.footfall_visits_credits",
    footfallVisitorsConversionTime: "explore_conversion.footfall_visitors",
    footfallVisitsConversionTime: "explore_conversion.footfall_visits",
    pageViewConversions: "explore_conversion.up_page_view_conversions",
    leadConversions: "explore_conversion.up_lead_conversions",
    purchaseConversions: "explore_conversion.up_purchase_conversions",
    purchaseRevenue: "explore_conversion.up_purchase_revenue",
    roas: "explore_conversion.roas",
    addToCartValue: "explore_conversion.up_addtocart_value",
    linearOnlyAverageFrequency: "explore_overlap_conversion.linear_average_frequency",
    linearOnlyConversionRate: "explore_overlap_conversion.linear_only_conversion_rate",
    linearOnlyProjectedHouseholds: "explore_overlap_conversion.linear_projected_households",
    linearOnlyProjectedImpressions: "explore_overlap_conversion.linear_projected_impressions",
    linearOnlyProportionReach: "explore_overlap_conversion.linear_proportion_reach",
    households: "explore_overlap_conversion.households",
    linearSample: "explore_overlap_conversion.linear_sample",
    linearSampleMarket: "explore_overlap_conversion.linear_sample_market",
    ottAverageFrequency: "explore_overlap_conversion.ott_average_frequency",
    ottOnlyConversionRate: "explore_overlap_conversion.ott_only_conversion_rate",
    ottProjectedHouseholds: "explore_overlap_conversion.ott_projected_households",
    ottProjectedImpressions: "explore_overlap_conversion.ott_projected_impressions",
    ottProportionReach: "explore_overlap_conversion.ott_proportion_reach",
    overlapAverageFrequency: "explore_overlap_conversion.overlap_average_frequency",
    overlapConversionRate: "explore_overlap_conversion.overlap_conversion_rate",
    overlapProjectedHouseholds: "explore_overlap_conversion.overlap_projected_households",
    overlapProjectedImpressions: "explore_overlap_conversion.overlap_projected_impressions",
    overlapProportionReach: "explore_overlap_conversion.overlap_proportion_reach",
    linearAdvertiserId: "explore_linear_conversion.advertiser_id",
    linearAdvertiserName: "explore_linear_conversion.advertiser_name",
    linearBeaconId: "explore_linear_conversion.beacon_id",
    linearBeaconName: "explore_linear_conversion.beacon_name",
    linearCreativeCode: "explore_linear_conversion.creative_code",
    linearCreativeName: "explore_linear_conversion.creative_name",
    linearDaypartName: "explore_linear_conversion.daypart_name",
    linearImpressionDate: "explore_linear_conversion.impression_date",
    linearImpressionHourOfWeek: "explore_linear_conversion.impression_hour_of_week",
    linearImpressionHourOfDay12: "explore_linear_conversion.impression_hour_of_day12",
    linearImpressionHourOfDay24: "explore_linear_conversion.impression_hour_of_day24",
    linearImpressionMonth: "explore_linear_conversion.impression_month",
    linearImpressionMonthOfYear: "explore_linear_conversion.impression_month_of_year",
    linearImpressionWeek: "explore_linear_conversion.impression_week",
    linearImpressionWeekOfYear: "explore_linear_conversion.impression_week_of_year",
    linearImpressionYear: "explore_linear_conversion.impression_year",
    linearMetroName: "explore_linear_conversion.metro_name",
    linearMetroCode: "explore_linear_conversion.metro_code",
    linearNetworkName: "explore_linear_conversion.network_name",
    linearProgramName: "explore_linear_conversion.program_name",
    linearDaysToConversion: "explore_linear_conversion.days_to_conversion",
    linearFrequency: "explore_linear_conversion.frequency",
    linearConversions: "explore_linear_conversion.projected_conversions",
    linearUniqueConversions: "explore_linear_conversion.projected_unique_conversions",
    linearUniqueConversionRate: "explore_linear_conversion.unique_conversion_rate",
    linearConversionRate: "explore_linear_conversion.conversion_rate",
    linearAverageFrequency: "explore_linear_conversion.average_frequency",
    linearProjectedHouseholds: "explore_linear_conversion.projected_households",
    linearImpressions: "explore_linear_conversion.projected_impressions",
    linearReachRate: "explore_linear_conversion.reach_rate",
    linearReach: "explore_linear_conversion.projected_reach",
    footfallVisitDay: "explore_conversion.visit_date",
    footfallVisitDayOfWeek: "explore_conversion.visit_day_of_week",
    footfallCity: "explore_conversion.ff_city",
    footfallState: "explore_conversion.ff_state",
    footfallZipcode: "explore_conversion.ff_zip"
};
export const MEDIA_TYPE_TO_LOOKER_EXPLORE = {
    VIDEO: "explore_conversion",
    AUDIO: "explore_conversion",
    DISPLAY: "explore_conversion",
    LINEAR: "explore_linear_conversion",
    OVERLAP: "explore_overlap_conversion"
};
const ATTRIBUTION_FILTER_TO_PARSER = {
    dates: (filter) => ({
        "explore_conversion.flt_analysis_interval": parseDateFilter(filter)
    }),
    relativeDate: (filter) => ({
        "explore_conversion.flt_analysis_interval": parseScheduleDateFilter(filter.value)
    }),
    bucketDateRangeLength: (filter) => ({
        "explore_conversion.pmt_dynamic_range": parseNumberFilter(filter)
    }),
    reportType: (filter) => ({
        "explore_conversion.flt_media_type": parseStringFilter(filter, true)
    }),
    lookbackDays: (filter) => ({
        "explore_conversion.pmt_lookback_window": parseNumberFilter(filter)
    }),
    advertiserIds: (filter) => ({
        "explore_conversion.flt_advertiser_id": parseStringFilter(filter)
    }),
    lineitemIds: (filter) => ({
        "explore_conversion.flt_lineitem_id": parseStringFilter(filter)
    }),
    pixelIds: (filter) => ({
        "explore_conversion.flt_beacon_id": parseNullableFilter(filter)
    }),
    queryLabel: (filter) => ({
        "vw_parameters.pmt_query_label": parseStringFilter(filter)
    }),
    pixelType: (filter) => ({
        "explore_conversion.pmt_pixel_type": parseStringFilter(filter)
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    commercialIds: (filter) => ({}),
    metroIds: (filter) => ({
        "explore_conversion.flt_metro_code": parseStringFilter(filter)
    }),
    // Carries UI intent only; the pmt_use_expanded_table param is emitted in
    // buildAttributionQuery (STATIC-pixel gate), not as a direct Looker filter.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useExpandedTable: (filter) => ({})
};
const OVERLAP_ATTRIBUTION_FILTER_TO_PARSER = {
    dates: (filter) => ({
        "explore_overlap_conversion.flt_analysis_interval": parseDateFilter(filter)
    }),
    relativeDate: (filter) => ({
        "explore_overlap_conversion.flt_analysis_interval": parseScheduleDateFilter(filter.value)
    }),
    lookbackDays: (filter) => ({
        "explore_overlap_conversion.pmt_lookback_window": parseNumberFilter(filter)
    }),
    advertiserIds: (filter) => ({
        "explore_overlap_conversion.flt_advertiser_id": parseStringFilter(filter)
    }),
    lineitemIds: (filter) => ({
        "explore_overlap_conversion.flt_lineitem_id": parseStringFilter(filter)
    }),
    pixelIds: (filter) => ({
        "explore_overlap_conversion.flt_beacon_id": parseNullableFilter(filter)
    }),
    commercialIds: (filter) => ({
        "explore_overlap_conversion.flt_creative_id": parseStringFilter(filter)
    }),
    queryLabel: (filter) => ({
        "vw_parameters.pmt_query_label": parseStringFilter(filter)
    }),
    pixelType: (filter) => ({
        "explore_overlap_conversion.pmt_pixel_type": parseStringFilter(filter)
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reportType: (filter) => ({}),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bucketDateRangeLength: (filter) => ({}),
    metroIds: (filter) => ({
        "explore_overlap_conversion.flt_metro_code": parseStringFilter(filter)
    }),
    // Not applicable to the overlap explore; expanded table is STATIC-web only.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useExpandedTable: (filter) => ({})
};
const LINEAR_ATTRIBUTION_FILTER_TO_PARSER = {
    dates: (filter) => ({
        "explore_linear_conversion.flt_analysis_interval": parseDateFilter(filter)
    }),
    relativeDate: (filter) => ({
        "explore_linear_conversion.flt_analysis_interval": parseScheduleDateFilter(filter.value)
    }),
    advertiserIds: (filter) => ({
        "explore_linear_conversion.flt_advertiser_id": parseStringFilter(filter)
    }),
    commercialIds: (filter) => ({
        "explore_linear_conversion.flt_creative_id": parseStringFilter(filter)
    }),
    pixelIds: (filter) => ({
        "explore_linear_conversion.flt_beacon_id": parseNullableFilter(filter)
    }),
    queryLabel: (filter) => ({
        "vw_parameters.pmt_query_label": parseStringFilter(filter)
    }),
    pixelType: (filter) => ({
        "explore_linear_conversion.pmt_pixel_type": parseStringFilter(filter)
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reportType: (filter) => ({}),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bucketDateRangeLength: (filter) => ({}),
    lookbackDays: (filter) => ({
        "explore_linear_conversion.pmt_lookback_window": parseNumberFilter(filter)
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lineitemIds: (filter) => ({}),
    metroIds: (filter) => ({
        "explore_linear_conversion.flt_metro_code": parseStringFilter(filter)
    }),
    // Not applicable to the linear explore; expanded table is STATIC-web only.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useExpandedTable: (filter) => ({})
};
/**
 * Transforms a list of model fields into a list of explore dimensions
 * using the object mapper
 *
 * @param fields The list of fields that are gonna be requested from the explore
 * @returns A list of dimension names from the explore
 */
export const attributionModelFieldsToQueryDimensions = (fields) => {
    const queryDimensions = new Set();
    for (const field of fields) {
        queryDimensions.add(ATTRIBUTION_MODEL_TO_QUERY_MAP[field]);
        const fieldConfig = ATTRIBUTION_MODEL_FIELDS[field];
        // We need to include the ID field in the query for a set of dimensions that can end up being grouped together due to not having a unique name, by adding the ID we separate them into their unique elements
        if (fieldConfig.fetchWithField) {
            const idField = fieldConfig.fetchWithField;
            queryDimensions.add(ATTRIBUTION_MODEL_TO_QUERY_MAP[idField]);
        }
    }
    return Array.from(queryDimensions);
};
/**
 * Cart items come back as a JSON string, and as an empty list or items with no
 * name for events that have none. Treat those as no value.
 */
const normalizeEventParameter = (field, value) => {
    if (field !== "cartItems" || typeof value !== "string") {
        return value;
    }
    try {
        const items = JSON.parse(value);
        const hasName = Array.isArray(items) && items.some((item) => Boolean(item?.item_name ?? item?.itemName));
        return hasName ? value : undefined;
    }
    catch {
        return value || undefined;
    }
};
/**
 * This builds the model object using the provided fields and the raw response
 *
 * @param fields The list of fields that where requested from the explore
 * @param queryResponse The response data from the explore query
 * @returns An object containing the requested fields with their matching response value
 */
export const attributionQueryDimensionsToModel = (fields, queryResponse) => {
    let model = {};
    for (const field of fields) {
        model = {
            ...model,
            [field]: normalizeEventParameter(field, queryResponse[ATTRIBUTION_MODEL_TO_QUERY_MAP[field]])
        };
        const fieldConfig = ATTRIBUTION_MODEL_FIELDS[field];
        if (fieldConfig.fetchWithField) {
            const idField = fieldConfig.fetchWithField;
            model = { ...model, [idField]: queryResponse[ATTRIBUTION_MODEL_TO_QUERY_MAP[idField]] };
        }
    }
    return model;
};
export const modelFiltersToQueryFilters = (filters) => {
    if (!filters) {
        throw new Error("You must provide filters in order to query the attribution explore");
    }
    let queryFilters = {};
    for (const filter of filters) {
        const parser = ATTRIBUTION_FILTER_TO_PARSER[filter.field];
        queryFilters = { ...queryFilters, ...parser(filter) };
    }
    return queryFilters;
};
export const overlapModelFiltersToQueryFilters = (filters) => {
    if (!filters) {
        throw new Error("You must provide filters in order to query the overlap attribution explore");
    }
    let queryFilters = {};
    for (const filter of filters) {
        const parser = OVERLAP_ATTRIBUTION_FILTER_TO_PARSER[filter.field];
        queryFilters = { ...queryFilters, ...parser(filter) };
    }
    return queryFilters;
};
export const linearModelFiltersToQueryFilters = (filters) => {
    if (!filters) {
        throw new Error("You must provide filters in order to query the linear attribution explore");
    }
    let queryFilters = {};
    for (const filter of filters) {
        const parser = LINEAR_ATTRIBUTION_FILTER_TO_PARSER[filter.field];
        queryFilters = { ...queryFilters, ...parser(filter) };
    }
    return queryFilters;
};
export const REPORT_TYPE_TO_EXPLORE_FILTERS_PARSER = {
    VIDEO: modelFiltersToQueryFilters,
    AUDIO: modelFiltersToQueryFilters,
    DISPLAY: modelFiltersToQueryFilters,
    LINEAR: linearModelFiltersToQueryFilters,
    OVERLAP: overlapModelFiltersToQueryFilters
};
const URL_FILTER_PARSERS = {
    dates: (value) => {
        const start = value[0];
        const end = value[1];
        if (!start || !end) {
            return undefined;
        }
        const startDate = DateTime.fromJSDate(start).toFormat("yyyy/MM/dd");
        const endDate = DateTime.fromJSDate(end).toFormat("yyyy/MM/dd");
        return `${startDate},${endDate}`;
    },
    reportType: (value) => value,
    pixelType: (value) => value,
    lookbackDays: (value) => `${value}`,
    advertiserId: (value) => value,
    lineItemIds: (value) => value?.join(","),
    pixelIds: (value) => value?.join(","),
    campaignIds: (value) => value?.join(","),
    commercialIds: (value) => value?.join(","),
    metroIds: (value) => (value?.length ? value.join(",") : undefined),
    includeFootfall: (value) => (value ? "true" : undefined)
};
export const createDummyQueryFilters = (filters) => {
    if (!filters) {
        throw new Error("You must provide filters to create a query.");
    }
    const queryFilters = {};
    for (const key of Object.keys(URL_FILTER_PARSERS)) {
        if (key in filters) {
            const parser = URL_FILTER_PARSERS[key];
            const value = filters[key];
            const result = parser(value);
            if (result !== undefined) {
                queryFilters[key] = result;
            }
        }
    }
    return queryFilters;
};
export const parseDateFilter = (filter) => {
    let queryFilter = "";
    switch (filter.type) {
        case FilterTypes.BETWEEN: {
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${filter.field} must be an array of dates when filtering for a date range`);
            }
            const [startDate, endDate] = filter.value;
            if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
                throw new Error(`Filter ${filter.field} must contain two dates when filtering for a date range`);
            }
            const formattedStartDate = DateTime.fromJSDate(startDate).toFormat("yyyy/MM/dd");
            // We add one day to the end date because Looker uses the end date as an exclusive filter, they show a message
            // in the UI that says "until (before)" which means that the end date is not included in the filter
            const formattedEndDate = DateTime.fromJSDate(endDate).plus({ day: 1 }).toFormat("yyyy/MM/dd");
            queryFilter = `${formattedStartDate} to ${formattedEndDate}`;
            break;
        }
    }
    return queryFilter;
};
export const isReportType = (v) => v === "VIDEO" || v === "AUDIO" || v === "DISPLAY" || v === "LINEAR" || v === "OVERLAP";
export const parseReportTypeFilter = (where) => {
    const filter = where?.find((f) => f.field === "reportType");
    if (!filter) {
        throw new Error("Report Type filter is required");
    }
    const rawValues = Array.isArray(filter.value) ? filter.value : [filter.value];
    const invalid = rawValues.filter((v) => !isReportType(v));
    if (invalid.length > 0) {
        throw new Error(`Invalid report type(s): ${invalid.map(String).join(", ")}`);
    }
    const reportTypes = rawValues.filter(isReportType).slice().sort();
    // OVERLAP is mutually exclusive with all other report types
    if (reportTypes.includes("OVERLAP") && reportTypes.length > 1) {
        throw new Error("OVERLAP cannot be combined with other report types");
    }
    // OVERLAP routes to the VIDEO digital explore
    if (reportTypes.includes("OVERLAP")) {
        return {
            lookerMediaTypeFilter: { "conversion_filter_suggests.media_type": "VIDEO" },
            reportTypes
        };
    }
    return {
        lookerMediaTypeFilter: { "conversion_filter_suggests.media_type": reportTypes.join(",") },
        reportTypes
    };
};
const parseStringFilter = (filter, sort) => {
    switch (filter.type) {
        case FilterTypes.EQ: {
            if (typeof filter.value !== "string") {
                throw new Error(`Filter ${filter.field} must be a string`);
            }
            return filter.value;
        }
        case FilterTypes.IN: {
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${filter.field} must be an array of strings`);
            }
            const values = sort ? [...filter.value].sort() : filter.value;
            return values.join(",");
        }
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${filter.field}`);
    }
};
const parseNullableFilter = (filter) => {
    const value = parseStringFilter(filter);
    return value === "" ? "NULL" : value;
};
const parseNumberFilter = (filter) => {
    switch (filter.type) {
        case FilterTypes.EQ: {
            if (typeof filter.value !== "number") {
                throw new Error(`Filter ${filter.field} must be a number`);
            }
            return filter.value.toString();
        }
        case FilterTypes.IN: {
            if (!Array.isArray(filter.value)) {
                throw new Error(`Filter ${filter.field} must be an array of numbers`);
            }
            return filter.value.join(",");
        }
        default:
            throw new Error(`Filter ${filter.type} is not supported for the field ${filter.field}`);
    }
};
const isLinearAdvertiserQueryResponseItem = (item) => {
    return (item &&
        typeof item === "object" &&
        "conversion_linear_suggests.advertiser_id" in item &&
        "conversion_linear_suggests.advertiser_name" in item);
};
export const isLinearAdvertiserQueryResponse = (response) => {
    return Array.isArray(response) && response.every(isLinearAdvertiserQueryResponseItem);
};
export const isOverlapQueryFilters = (filters) => {
    return !!filters["explore_overlap_conversion.flt_advertiser_id"];
};
export const isLinearQueryFilters = (filters) => {
    return !!filters["explore_linear_conversion.flt_advertiser_id"];
};
export const isOttAudioOrDisplayQueryFilters = (filters) => {
    return !!filters["explore_conversion.flt_advertiser_id"];
};
/**
 * Field mapping: `ConversionFilterSuggestsModel` → Looker field names.
 * `satisfies` rather than an annotation keeps the values literal, so a typo'd
 * Looker key fails to compile instead of widening to `keyof
 * ConversionFilterSuggestsFields`.
 */
export const CONVERSION_FILTER_SUGGESTS_FIELD_MAP = {
    advertiserName: "conversion_filter_suggests.advertiser_name",
    advertiserId: "conversion_filter_suggests.advertiser_id",
    campaignName: "conversion_filter_suggests.campaign_name",
    campaignId: "conversion_filter_suggests.campaign_id",
    lineitemId: "conversion_filter_suggests.lineitem_id",
    beaconName: "conversion_meta_beacon.name",
    beaconId: "conversion_filter_suggests.beacon_id",
    beaconStatus: "conversion_meta_beacon.beacon_status",
    pixelType: "conversion_filter_suggests.pixel_type"
};
/**
 * Filter mapping: API filter names → Looker filter keys
 */
export const CONVERSION_FILTER_SUGGESTS_FILTER_MAP = {
    analysis_interval: "conversion_filter_suggests.filter_date_nyc",
    report_type: "conversion_filter_suggests.media_type",
    advertiser_id: "conversion_filter_suggests.advertiser_id"
};
/**
 * Field mapping: `LinearConversionFilterSuggestsModel` → Looker field names.
 * `satisfies` rather than an annotation keeps the values literal — see
 * `CONVERSION_FILTER_SUGGESTS_FIELD_MAP`'s doc comment above.
 */
export const LINEAR_CONVERSION_FILTER_SUGGESTS_FIELD_MAP = {
    advertiserId: "conversion_linear_suggests.advertiser_id",
    advertiserName: "conversion_linear_suggests.advertiser_name",
    creativeId: "conversion_linear_suggests.creative_id",
    creativeName: "conversion_linear_suggests.creative_name"
};
/**
 * Filter mapping: API filter names → Looker filter keys
 */
export const LINEAR_CONVERSION_FILTER_SUGGESTS_FILTER_MAP = {
    analysis_interval: "conversion_linear_suggests.flt_date_nyc",
    advertiser_id: "conversion_linear_suggests.advertiser_id"
};
/**
 * Field mapping
 */
export const DELIVERY_FILTER_SUGGESTS_FIELD_MAP = {
    advertiser_name: CONVERSION_FILTER_SUGGESTS_FIELD_MAP.advertiserName,
    advertiser_id: CONVERSION_FILTER_SUGGESTS_FIELD_MAP.advertiserId,
    campaign_name: CONVERSION_FILTER_SUGGESTS_FIELD_MAP.campaignName,
    campaign_id: CONVERSION_FILTER_SUGGESTS_FIELD_MAP.campaignId,
    lineitem_id: CONVERSION_FILTER_SUGGESTS_FIELD_MAP.lineitemId
};
/**
 * Every delivery-filter-options field name. `DELIVERY_FILTER_SUGGESTS_FIELD_MAP`
 * is already keyed by API name, so this is just its key set, named.
 */
export const ALL_DELIVERY_FILTER_SUGGESTS_FIELD_NAMES = Object.keys(DELIVERY_FILTER_SUGGESTS_FIELD_MAP);
export const DELIVERY_OPTIONS_REQUIRED_FILTERS = [
    "analysis_interval"
];
/**
 * Filter mapping
 */
export const DELIVERY_FILTER_SUGGESTS_FILTER_MAP = {
    analysis_interval: CONVERSION_FILTER_SUGGESTS_FILTER_MAP.analysis_interval,
    report_type: CONVERSION_FILTER_SUGGESTS_FILTER_MAP.report_type,
    advertiser_id: CONVERSION_FILTER_SUGGESTS_FILTER_MAP.advertiser_id
};
/** ------------------------------------------------- */
/** Filter Options - Utility Functions */
/** ------------------------------------------------- */
/**
 * Converts Looker query response row to DeliveryFilterSuggestsModel
 */
export const queryFieldsToDeliveryFilterSuggestsModel = (row) => {
    const model = {};
    for (const [modelKey, lookerField] of Object.entries(DELIVERY_FILTER_SUGGESTS_FIELD_MAP)) {
        if (lookerField in row) {
            model[modelKey] = row[lookerField];
        }
    }
    return model;
};
/**
 * Filters dimension fields based on pixel type and per-field rollout flags.
 * Three independent rules:
 *   1. Non-UNIVERSAL pixel types drop every isUniversalPixelField dim
 *      (UP-only dims have no meaning outside Universal Pixel).
 *   2. The UP conversion events rollout flag specifically gates the
 *      isUpConversionEventField dims (conversionType, eventType). Pre-existing
 *      UP dims (beaconPath, beaconUrl) stay visible whenever pixel type is
 *      UNIVERSAL, regardless of the flag.
 *   3. The vehicle details rollout flag specifically gates the
 *      isVehicleDetailsField dims (vin, make, model, year), independently of the
 *      conversion events flag.
 *
 * Same rules `isAttributionFieldAllowed` in policy.ts applies to any field;
 * this one is dimension-scoped and takes its flags as arguments rather than a
 * policy context, which is what the tile dropdowns have to hand.
 */
export const getAllowedDimensions = (dimensions, pixelType, isUniversalPixelConversionEventsEnabled, isVehicleDetailsEnabled = false) => {
    return dimensions.filter((dim) => {
        const config = ATTRIBUTION_MODEL_FIELDS[dim];
        if (pixelType !== "UNIVERSAL" && config.isUniversalPixelField) {
            return false;
        }
        if (!isUniversalPixelConversionEventsEnabled && config.isUpConversionEventField) {
            return false;
        }
        if (!isVehicleDetailsEnabled && config.isVehicleDetailsField) {
            return false;
        }
        return true;
    });
};
//# sourceMappingURL=index.js.map